import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Breadcrumbs,
  Link as MuiLink,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { useApiService } from '../../hooks/useApiService';
import { useKeepGrantedTab } from '../../brand/MenuPolicyContext';
import { POOL_SETTINGS_TAB_IDS, hiddenTabProps } from '../../brand/menuCatalog';
import { Pool } from '../../types';
import type { PoolSettings, PoolSettingsUpdate } from '../../types/pool';
import WaterQualitySettingsForm from '../../components/pool/WaterQualitySettingsForm';
import SafetySettingsForm from '../../components/pool/SafetySettingsForm';
import NotificationSettingsForm from '../../components/pool/NotificationSettingsForm';
import SafetyAlertDispatchHistoryCard from '../../components/safety/SafetyAlertDispatchHistoryCard';
import ScheduleSettingsForm from '../../components/pool/ScheduleSettingsForm';
import AutoControlSettingsForm from '../../components/pool/AutoControlSettingsForm';
import CameraLayoutSimulator from '../../components/pool/CameraLayoutSimulator';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pool-settings-tabpanel-${index}`}
      aria-labelledby={`pool-settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const PoolSettingsPage: React.FC = () => {
  const { poolCode } = useParams<{ poolCode: string }>();
  const navigate = useNavigate();
  const api = useApiService();
  const isMounted = useRef(true);
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const hideTabs = searchParams.get('hideTabs') === 'true';

  const [pool, setPool] = useState<Pool | null>(null);
  const [settings, setSettings] = useState<PoolSettings | null>(null);
  const [settingsUpdate, setSettingsUpdate] = useState<PoolSettingsUpdate>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const settingsTabOn = useKeepGrantedTab(tabValue, POOL_SETTINGS_TAB_IDS, setTabValue);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  useEffect(() => {
    isMounted.current = true;

    const fetchData = async () => {
      if (!isMounted.current) return;

      if (!poolCode) {
        if (isMounted.current) {
          setError("URL에서 시설 코드를 찾기 불가");
          setLoading(false);
        }
        return;
      }

      if (isMounted.current) {
        setLoading(true);
        setError(null);
      }

      try {
        const poolResponse = await api.poolApi.getPoolByCode(poolCode);
        if (!isMounted.current) return;

        if (!poolResponse.success || !poolResponse.data) {
          throw new Error(poolResponse.message || '시설 정보를 불러오기 실패');
        }
        const poolData = poolResponse.data;
        setPool(poolData);

        const numericPoolId = Number(poolData.id);
        if (isNaN(numericPoolId)) {
          throw new Error('시설 정보에서 유효한 숫자 ID를 찾기 불가');
        }

        const settingsResponse = await api.poolApi.getPoolSettings(numericPoolId);
        if (!isMounted.current) return;

        if (!settingsResponse.success) {
          throw new Error(settingsResponse.message || '환경설정 정보를 불러오기 실패');
        }
        
        // 응답 데이터 처리
        console.log('원본 settings 응답:', settingsResponse);
        let settingsData = null;
        
        // 응답 구조에 따른 처리
        if (settingsResponse.data && typeof settingsResponse.data === 'object') {
          if ('data' in settingsResponse.data) {
            // { success: true, data: { data: {...} } } 구조
            settingsData = settingsResponse.data.data;
          } else {
            // { success: true, data: {...} } 구조
            settingsData = settingsResponse.data;
          }
        }
        
        if (settingsData) {
          console.log('변환 전 원본 설정 데이터:', settingsData);
          
          // 데이터 구조 확인
          const isCorrectFormat = settingsData.water_quality_thresholds && 
                                settingsData.safety_thresholds && 
                                settingsData.notification_settings;
          
          if (isCorrectFormat) {
            // 올바른 형식이면 그대로 사용
            console.log('이미 올바른 형식의 설정 데이터입니다');
            setSettings(settingsData);
          } else {
            // 백엔드에서 온 형식을 프론트엔드 형식으로 변환
            console.log('설정 데이터 변환 필요, 변환 시작...');
            
            // 더미 템플릿 생성 (필요한 모든 필드 포함)
            const convertedSettings: PoolSettings = {
              id: 1,
              pool_id: numericPoolId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              
              // 수질 기준
              water_quality_thresholds: {
                ph: { 
                  min: 7.2, 
                  max: 7.8 
                },
                chlorine: { 
                  min: 0.5, 
                  max: 2.0 
                },
                temperature: { 
                  min: settingsData.temperature_min || 25, 
                  max: settingsData.temperature_max || 28 
                },
                turbidity: { 
                  min: 0, 
                  max: 1.0 
                }
              },
              
              // 안전 기준
              safety_thresholds: {
                max_occupancy: settingsData.max_capacity_warning || 100,
                lifeguard_count: 2,
                max_depth: 2.0,
                emergency_equipment_count: 5
              },
              
              // 알림 설정
              notification_settings: {
                email_alerts: true,
                sms_alerts: true,
                app_alerts: true,
                webhook_alerts: false,
                alert_frequency: "immediate"
              },
              
              // 자동 제어 설정
              auto_control: {
                enabled: settingsData.auto_alert || true,
                temperature_control: true,
                chlorine_control: true,
                ph_control: true
              },
              
              // 운영 일정
              schedule: {
                opening_time: (settingsData.operating_hours || "09:00-18:00").split('-')[0] || "06:00",
                closing_time: (settingsData.operating_hours || "09:00-18:00").split('-')[1] || "22:00",
                maintenance_day: "monday",
                maintenance_time: "22:00-06:00"
              }
            };
            
            console.log('변환된 설정 데이터:', convertedSettings);
            setSettings(convertedSettings);
          }
        } else {
          throw new Error('유효한 설정 데이터 없음');
        }

      } catch (err) {
        if (isMounted.current) {
          console.error('Error loading pool settings:', err);
          setError(err instanceof Error ? err.message : '환경설정을 불러오기 실패');
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted.current = false;
      console.log(`[PoolSettings Cleanup] Unmounting or poolCode changed: ${poolCode}`);
    };

  }, [poolCode, api.poolApi]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSettingChange = useCallback(<K extends keyof PoolSettingsUpdate>(key: K, data: PoolSettingsUpdate[K]) => {
    setSettingsUpdate(prev => ({
      ...prev,
      [key]: data,
    }));
  }, []);

  const handleWaterQualityChange = useCallback((data: PoolSettingsUpdate['water_quality_thresholds']) => {
    handleSettingChange('water_quality_thresholds', data);
  }, [handleSettingChange]);

  const handleSafetyChange = useCallback((data: PoolSettingsUpdate['safety_thresholds']) => {
    handleSettingChange('safety_thresholds', data);
  }, [handleSettingChange]);

  const handleNotificationChange = useCallback((data: PoolSettingsUpdate['notification_settings']) => {
    handleSettingChange('notification_settings', data);
  }, [handleSettingChange]);

  const handleAutoControlChange = useCallback((data: PoolSettingsUpdate['auto_control']) => {
    handleSettingChange('auto_control', data);
  }, [handleSettingChange]);

  const handleScheduleChange = useCallback((data: PoolSettingsUpdate['schedule']) => {
    handleSettingChange('schedule', data);
  }, [handleSettingChange]);

  const handleSaveSettings = async () => {
    if (!pool || !settings || typeof pool.id !== 'number') {
      console.error('Cannot save settings: Pool data or numeric ID is missing.', { pool, settings });
      showSnackbar('설정 저장 실패: 유효한 시설 정보 없음', 'error');
      return;
    }
    if (Object.keys(settingsUpdate).length === 0) {
      showSnackbar('변경된 설정 없음', 'info');
      return;
    }

    const numericPoolId = pool.id;
    setSaving(true);

    try {
      const response = await api.poolApi.updatePoolSettings(numericPoolId, settingsUpdate);

      if (response.success && response.data) {
        // API 응답 데이터를 PoolSettings 타입으로 변환
        const apiData = response.data as Record<string, any>;
        const convertedData: PoolSettings = {
          id: apiData.id,
          pool_id: apiData.pool_id,
          water_quality_thresholds: {
            ph: apiData.water_quality_thresholds?.ph || { min: 7.2, max: 7.8 },
            chlorine: apiData.water_quality_thresholds?.chlorine || { min: 1.0, max: 3.0 },
            temperature: apiData.water_quality_thresholds?.temperature || { min: 26, max: 30 },
            turbidity: apiData.water_quality_thresholds?.turbidity || { min: 0, max: 1.0 }
          },
          safety_thresholds: {
            max_occupancy: apiData.safety_thresholds?.max_occupancy || apiData.max_capacity,
            lifeguard_count: apiData.safety_thresholds?.lifeguard_count || 2,
            max_depth: apiData.safety_thresholds?.max_depth || 2.0,
            emergency_equipment_count: apiData.safety_thresholds?.emergency_equipment_count || 5
          },
          notification_settings: {
            email_alerts: apiData.notification_settings?.email_alerts || false,
            sms_alerts: apiData.notification_settings?.sms_alerts || false,
            app_alerts: apiData.notification_settings?.app_alerts || false,
            webhook_alerts: apiData.notification_settings?.webhook_alerts || false,
            alert_frequency: apiData.notification_settings?.alert_frequency || 'immediate'
          },
          auto_control: {
            enabled: apiData.auto_control?.enabled || true,
            temperature_control: apiData.auto_control?.temperature_control || true,
            chlorine_control: apiData.auto_control?.chlorine_control || true,
            ph_control: apiData.auto_control?.ph_control || true
          },
          schedule: {
            opening_time: apiData.schedule?.opening_time || apiData.operating_hours?.open || '06:00',
            closing_time: apiData.schedule?.closing_time || apiData.operating_hours?.close || '22:00',
            maintenance_day: apiData.schedule?.maintenance_day || 'monday',
            maintenance_time: apiData.schedule?.maintenance_time || '22:00-06:00'
          },
          created_at: apiData.created_at,
          updated_at: apiData.updated_at
        };
        setSettings(convertedData);
        setSettingsUpdate({});
        showSnackbar('환경설정이 저장 완료', 'success');
      } else {
        throw new Error(response.message || '환경설정 저장에 실패');
      }
    } catch (err) {
      console.error('Error saving pool settings:', err);
      showSnackbar(err instanceof Error ? err.message : '환경설정 저장에 실패', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>설정 정보를 불러오는 중...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            다시 시도
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!pool) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">시설 정보를 찾을 수 없음.</Alert>
      </Container>
    );
  }

  const hasChanges = Object.keys(settingsUpdate).length > 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving || !hasChanges}
          >
            {saving ? '저장 중...' : '변경사항 저장'}
          </Button>
        </Box>

        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'block !important',
          visibility: 'visible !important',
          opacity: 1,
          position: 'relative',
          zIndex: 1
        }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="pool settings tabs"
            sx={{
              display: 'flex !important',
              visibility: 'visible !important',
              opacity: 1
            }}
          >
            <Tab label="수질 기준" id="pool-settings-tab-0" aria-controls="pool-settings-tabpanel-0" {...hiddenTabProps(settingsTabOn(0))} />
            <Tab label="안전 기준" id="pool-settings-tab-1" aria-controls="pool-settings-tabpanel-1" {...hiddenTabProps(settingsTabOn(1))} />
            <Tab label="알림 설정" id="pool-settings-tab-2" aria-controls="pool-settings-tabpanel-2" {...hiddenTabProps(settingsTabOn(2))} />
            <Tab label="자동 제어" id="pool-settings-tab-3" aria-controls="pool-settings-tabpanel-3" {...hiddenTabProps(settingsTabOn(3))} />
            <Tab label="운영 일정" id="pool-settings-tab-4" aria-controls="pool-settings-tabpanel-4" {...hiddenTabProps(settingsTabOn(4))} />
            <Tab label="카메라 배치" id="pool-settings-tab-5" aria-controls="pool-settings-tabpanel-5" {...hiddenTabProps(settingsTabOn(5))} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {settings?.water_quality_thresholds ? (
            <WaterQualitySettingsForm
              settings={{
                ph: {
                  min: settingsUpdate.water_quality_thresholds?.ph?.min ?? settings.water_quality_thresholds.ph.min,
                  max: settingsUpdate.water_quality_thresholds?.ph?.max ?? settings.water_quality_thresholds.ph.max,
                },
                chlorine: {
                  min: settingsUpdate.water_quality_thresholds?.chlorine?.min ?? settings.water_quality_thresholds.chlorine.min,
                  max: settingsUpdate.water_quality_thresholds?.chlorine?.max ?? settings.water_quality_thresholds.chlorine.max,
                },
                temperature: {
                  min: settingsUpdate.water_quality_thresholds?.temperature?.min ?? settings.water_quality_thresholds.temperature.min,
                  max: settingsUpdate.water_quality_thresholds?.temperature?.max ?? settings.water_quality_thresholds.temperature.max,
                },
                turbidity: settings.water_quality_thresholds.turbidity ? {
                  min: settingsUpdate.water_quality_thresholds?.turbidity?.min ?? settings.water_quality_thresholds.turbidity.min,
                  max: settingsUpdate.water_quality_thresholds?.turbidity?.max ?? settings.water_quality_thresholds.turbidity.max,
                } : undefined,
              }}
              onChange={handleWaterQualityChange}
            />
          ) : (
            <Typography>수질 기준 설정 정보를 불러오는 중...</Typography>
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {settings?.safety_thresholds ? (
            <SafetySettingsForm
              settings={{
                max_occupancy: settingsUpdate.safety_thresholds?.max_occupancy ?? settings.safety_thresholds.max_occupancy,
                lifeguard_count: settingsUpdate.safety_thresholds?.lifeguard_count ?? settings.safety_thresholds.lifeguard_count,
                max_depth: settingsUpdate.safety_thresholds?.max_depth ?? settings.safety_thresholds.max_depth,
                emergency_equipment_count: settingsUpdate.safety_thresholds?.emergency_equipment_count ?? settings.safety_thresholds.emergency_equipment_count,
              }}
              onChange={handleSafetyChange}
            />
          ) : (
            <Typography>안전 기준 설정 정보를 불러오는 중...</Typography>
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          {settings?.notification_settings ? (
            <>
              <NotificationSettingsForm
                settings={{
                  email_alerts: settingsUpdate.notification_settings?.email_alerts ?? settings.notification_settings.email_alerts,
                  sms_alerts: settingsUpdate.notification_settings?.sms_alerts ?? settings.notification_settings.sms_alerts,
                  app_alerts: settingsUpdate.notification_settings?.app_alerts ?? settings.notification_settings.app_alerts,
                  webhook_alerts: settingsUpdate.notification_settings?.webhook_alerts ?? settings.notification_settings.webhook_alerts,
                  alert_frequency: settingsUpdate.notification_settings?.alert_frequency ?? settings.notification_settings.alert_frequency,
                }}
                onChange={handleNotificationChange}
              />
              {pool?.id != null && Number.isFinite(Number(pool.id)) ? (
                <Box sx={{ mt: 3 }}>
                  <SafetyAlertDispatchHistoryCard
                    poolId={Number(pool.id)}
                    poolName={pool.name}
                    showPoolColumn={false}
                    title="현장 안전 알림 발송 이력"
                    subheader="이 시설에서 발송한 SMS·카카오·웨어러블 결과"
                  />
                </Box>
              ) : null}
            </>
          ) : (
            <Typography>알림 설정 정보를 불러오는 중...</Typography>
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          {settings?.auto_control ? (
            <AutoControlSettingsForm
              settings={{
                enabled: settingsUpdate.auto_control?.enabled ?? settings.auto_control.enabled,
                temperature_control: settingsUpdate.auto_control?.temperature_control ?? settings.auto_control.temperature_control,
                chlorine_control: settingsUpdate.auto_control?.chlorine_control ?? settings.auto_control.chlorine_control,
                ph_control: settingsUpdate.auto_control?.ph_control ?? settings.auto_control.ph_control,
              }}
              onChange={handleAutoControlChange}
            />
          ) : (
            <Typography>자동 제어 설정 정보를 불러오는 중...</Typography>
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          {settings?.schedule ? (
            <ScheduleSettingsForm
              settings={{
                opening_time: settingsUpdate.schedule?.opening_time ?? settings.schedule.opening_time,
                closing_time: settingsUpdate.schedule?.closing_time ?? settings.schedule.closing_time,
                maintenance_day: settingsUpdate.schedule?.maintenance_day ?? settings.schedule.maintenance_day,
                maintenance_time: settingsUpdate.schedule?.maintenance_time ?? settings.schedule.maintenance_time,
              }}
              onChange={handleScheduleChange}
            />
          ) : (
            <Typography>운영 일정 설정 정보를 불러오는 중...</Typography>
          )}
        </TabPanel>
        <TabPanel value={tabValue} index={5}>
          {pool && typeof pool.id === 'number' ? (
            <CameraLayoutSimulator poolId={pool.id} poolName={pool.name} />
          ) : (
            <Typography>시설 ID가 필요.</Typography>
          )}
        </TabPanel>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PoolSettingsPage; 